/**
 * analytics-router.ts — единая точка выбора API-источника для аналитики.
 *
 * Правило: если пользователь имеет event-роль и передан eventId —
 * используем event-scoped эндпоинты (organizer / event-admin).
 * Иначе — платформенные эндпоинты (InternalRoleGuard).
 *
 * Все публичные функции возвращают данные, нормализованные
 * к типам, ожидаемым analyticsSlice.
 */

import { RoleType } from '../../types/backend/auth.types';
import { EventRole } from '../../types/enums/event-roles.enum';
import {
  ParticipantAnalyticsEntry,
  ParticipantAnalyticsFilters,
  ParticipantAnalyticsPagination,
  ParticipantAnalyticsMetrics,
  ParticipantSegmentation,
  EventAnalyticsEntry,
  EventAnalyticsFilters,
  EventAnalyticsPagination,
  EventAnalyticsMetrics,
  ConversionAnalyticsFilters,
  ConversionFunnelStep,
  ConversionAnalyticsMetrics,
} from '../../types/analytics';
import participantsAnalyticsApi from './participants-analytics.api';
import eventsAnalyticsApi from './events-analytics.api';
import {
  organizerAnalyticsApi,
  eventAdminAnalyticsApi,
  type GenerateEventReportParams,
  type EventReportResponse,
} from './event-analytics.api';
import type { ApiResponse } from '../../types/responses/api-response';

// ─── Scope resolution ────────────────────────────────────────────────────────

export type AnalyticsScope = 'platform' | 'organizer' | 'event_admin';

export function resolveAnalyticsScope(
  roleType: string | undefined,
  role: string | undefined,
  eventId: string | null | undefined,
): AnalyticsScope {
  if (roleType !== RoleType.EVENT || !eventId) return 'platform';
  return role === EventRole.EVENT_ADMIN ? 'event_admin' : 'organizer';
}

// ─── Participants ─────────────────────────────────────────────────────────────

export interface ScopedParticipantsResult {
  participants: ParticipantAnalyticsEntry[];
  pagination: ParticipantAnalyticsPagination;
  filters: ParticipantAnalyticsFilters;
}

export async function getScopedParticipants(
  scope: AnalyticsScope,
  filters: ParticipantAnalyticsFilters,
): Promise<ScopedParticipantsResult> {
  if (scope !== 'platform' && filters.eventId) {
    const res = scope === 'event_admin'
      ? await eventAdminAnalyticsApi.getParticipants(filters.eventId, filters.page, filters.limit)
      : await organizerAnalyticsApi.getParticipants(filters.eventId);
    const payload = res.data as { participants?: ParticipantAnalyticsEntry[] } | ParticipantAnalyticsEntry[] | null;
    const items: ParticipantAnalyticsEntry[] = Array.isArray(payload)
      ? payload
      : (payload as { participants?: ParticipantAnalyticsEntry[] })?.participants ?? [];
    return {
      participants: items,
      pagination: {
        page: filters.page ?? 1,
        limit: filters.limit ?? items.length,
        total: items.length,
        totalPages: 1,
      },
      filters,
    };
  }
  const res = await participantsAnalyticsApi.getParticipants(filters);
  return {
    participants: res.data.participants as ParticipantAnalyticsEntry[],
    pagination: res.data.pagination,
    // Return the inbound filters — the API response filters use different field names
    filters,
  };
}

export async function getScopedParticipantMetrics(
  scope: AnalyticsScope,
  filters: ParticipantAnalyticsFilters,
): Promise<ParticipantAnalyticsMetrics> {
  if (scope !== 'platform' && filters.eventId) {
    // AD-360. Контракты у двух источников разные, поэтому из каждого берём
    // только то, что в нём есть. Прежний код читал поля `participantsCount`,
    // `engagementRate` и `budgetSpent`, которых нет ни у одного из них, и
    // превращал полученный `undefined` в ноль: экран показывал «удержание 0 %»
    // и «выручка 0» как измеренные значения. Ни вовлечённости, ни выручки, ни
    // активности эти эндпоинты не считают — этих групп в ответе просто нет.
    if (scope === 'event_admin') {
      const res = await eventAdminAnalyticsApi.getMetrics(filters.eventId);
      const d = res.data?.data;
      if (!d) return {};
      return {
        overview: { totalParticipants: d.totalParticipants },
        // `dailyRegistrations` — единственный ряд, который сервер здесь отдаёт.
        trends: {
          registrationTrend: d.dailyRegistrations.map((day) => ({
            date: day.date,
            count: day.registrations,
          })),
        },
      };
    }
    const res = await organizerAnalyticsApi.getMetrics(filters.eventId);
    const d = res.data?.data;
    if (!d) return {};
    return { overview: { totalParticipants: d.totalParticipants } };
  }
  const res = await participantsAnalyticsApi.getParticipantsMetrics(filters);
  return res.data as ParticipantAnalyticsMetrics;
}

export async function getScopedParticipantSegmentation(
  scope: AnalyticsScope,
  filters: ParticipantAnalyticsFilters,
): Promise<ParticipantSegmentation> {
  if (scope !== 'platform') {
    return {
      byAge: [],
      byLocation: [],
      byActivity: [],
      bySpending: [],
      byEngagement: [],
    } as ParticipantSegmentation;
  }
  const res = await participantsAnalyticsApi.getParticipantsSegmentation(filters);
  return res.data as ParticipantSegmentation;
}

// ─── Events ───────────────────────────────────────────────────────────────────

export interface ScopedEventsResult {
  events: EventAnalyticsEntry[];
  pagination: EventAnalyticsPagination;
  filters: EventAnalyticsFilters;
}

export async function getScopedEvents(
  scope: AnalyticsScope,
  filters: EventAnalyticsFilters,
): Promise<ScopedEventsResult> {
  if (scope !== 'platform' && filters.eventId) {
    if (scope === 'event_admin') {
      const res = await eventAdminAnalyticsApi.getMetrics(filters.eventId);
      const d = res.data?.data;
      // AD-360. О самом мероприятии контракт `/event-admin/metrics` знает ровно
      // две вещи из тех, что показывает список: название и число участников.
      // Ни типа, ни статуса, ни дат, ни выручки там нет — и подставлять их
      // нечем: прежний код писал в название идентификатор события, в статус —
      // литерал «активно», а в выручку и конверсию клал расход бюджета и
      // вовлечённость, то есть чужие измерения под чужими именами.
      const entry: EventAnalyticsEntry = {
        id: d?.eventId ?? filters.eventId,
        ...(d ? { title: d.eventTitle, participantsCount: d.totalParticipants } : {}),
      };
      return {
        events: [entry],
        pagination: { page: 1, limit: 1, total: 1, totalPages: 1 },
        filters,
      };
    }
    const res = await organizerAnalyticsApi.getAnalytics(filters.eventId);
    // `topEvents` из `/organizer/analytics` — четыре поля, и все необязательные
    // (см. генерат `OrganizerGetAnalyticsResponse`). Запись без идентификатора
    // пропускаем: она не адресует мероприятие.
    const topEvents = res.data?.topEvents ?? [];
    const mapped: EventAnalyticsEntry[] = topEvents.flatMap((e) =>
      e.id === undefined
        ? []
        : [
            {
              id: e.id,
              ...(e.title !== undefined ? { title: e.title } : {}),
              ...(e.participants !== undefined
                ? { participantsCount: e.participants }
                : {}),
              ...(e.rating !== undefined ? { averageRating: e.rating } : {}),
            },
          ],
    );
    return {
      events: mapped,
      pagination: { page: 1, limit: mapped.length, total: mapped.length, totalPages: 1 },
      filters,
    };
  }
  const res = await eventsAnalyticsApi.getEvents(filters);
  return {
    events: res.data.events as EventAnalyticsEntry[],
    pagination: res.data.pagination,
    // Return the inbound filters — the API response filters use different field names
    filters,
  };
}

// ─── Conversions ─────────────────────────────────────────────────────────────
//
// No dedicated event-scoped conversion endpoint exists on the BE yet (Phase 3).
// Both platform and event-scoped calls go through the platform API with eventId,
// which the BE already uses for filtering. When the event endpoint is added, only
// this router needs to change — callers are already isolated.

// BE funnel step shape (differs from frontend ConversionFunnelStep)
interface BEFunnelStep {
  stage: string;
  count: number;
  percentage: number;
  conversionRate: number;
}

type ConversionFunnelApiResponse = {
  data?: { funnel?: BEFunnelStep[] };
  funnel?: BEFunnelStep[];
};

type ConversionMetricsApiResponse = { data?: ConversionAnalyticsMetrics } & Partial<ConversionAnalyticsMetrics>;

function normalizeFunnelStep(step: BEFunnelStep, index: number): ConversionFunnelStep {
  return {
    id: String(index + 1),
    eventId: '',
    eventTitle: '',
    step: String(index + 1),
    stepName: step.stage,
    users: step.count,
    conversions: Math.round(step.count * (step.conversionRate / 100)),
    conversionRate: step.conversionRate,
    dropOffRate: step.percentage,
    averageTime: 0,
  };
}

export async function getScopedConversionFunnel(
  _scope: AnalyticsScope,
  filters: ConversionAnalyticsFilters,
): Promise<ConversionFunnelStep[]> {
  const res = await eventsAnalyticsApi.getConversionFunnel(filters);
  const payload = res?.data as ConversionFunnelApiResponse | null;
  const inner = payload?.data ?? payload;
  const steps = inner?.funnel ?? [];
  return steps.map(normalizeFunnelStep);
}

export async function getScopedConversionMetrics(
  _scope: AnalyticsScope,
  filters: ConversionAnalyticsFilters,
): Promise<ConversionAnalyticsMetrics> {
  const res = await eventsAnalyticsApi.getConversionsMetrics(filters);
  const payload = res?.data as ConversionMetricsApiResponse | null;
  return (payload?.data ?? payload) as ConversionAnalyticsMetrics;
}

// ─── Reports (5.3) ───────────────────────────────────────────────────────────

/** Единая точка генерации отчётов — маршрутизация по scope без if-ов в компонентах */
export async function generateScopedReport(
  scope: AnalyticsScope,
  params: GenerateEventReportParams,
): Promise<ApiResponse<EventReportResponse>> {
  const api = scope === 'event_admin' ? eventAdminAnalyticsApi : organizerAnalyticsApi;
  return api.generateReport(params) as Promise<ApiResponse<EventReportResponse>>;
}

export async function getScopedEventMetrics(
  scope: AnalyticsScope,
  filters: EventAnalyticsFilters,
): Promise<EventAnalyticsMetrics> {
  if (scope !== 'platform' && filters.eventId) {
    // AD-360. «Всего мероприятий 1, активных 1» было литералом: ответ про ОДНО
    // мероприятие ничего не сообщает о его статусе, и счётчика активных в нём
    // нет. Выручки нет ни у одного из источников — у организатора есть бюджет,
    // а бюджет это не выручка, и подставлять его под чужим именем нельзя.
    if (scope === 'event_admin') {
      const res = await eventAdminAnalyticsApi.getMetrics(filters.eventId);
      const d = res.data?.data;
      if (!d) return {};
      return { participation: { totalParticipants: d.totalParticipants } };
    }
    const res = await organizerAnalyticsApi.getMetrics(filters.eventId);
    const d = res.data?.data;
    if (!d) return {};
    return {
      overview: {
        totalEvents: d.totalEvents,
        activeEvents: d.activeEvents,
        completedEvents: d.completedEvents,
        cancelledEvents: d.cancelledEvents,
      },
      participation: { totalParticipants: d.totalParticipants },
    };
  }
  const res = await eventsAnalyticsApi.getEventsMetrics(filters);
  return res.data as EventAnalyticsMetrics;
}
